'use client';

import type { Socket } from 'socket.io-client';
import type { RootState } from '@/redux/store';
import { createContext, use, useEffect, useMemo, useRef, useState } from 'react';
import { tokenManager } from '@/queries/token-manager';
import { useAppSelector } from '@/redux/hooks';
import { SocketListeners } from './components/socket-listeners';
import { createSocketManager } from './utils/websocket.utils';

type WebSocketContextType = {
  socket: Socket | null;
};

const WebSocketContext = createContext<WebSocketContextType>({ socket: null });

export const useSocket = () => use(WebSocketContext);

export const WebSocketProvider = ({ children }: { children: React.ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const userSession = useAppSelector((state: RootState) => state.user.userSession);
  const managerRef = useRef<ReturnType<typeof createSocketManager> | null>(null);

  useEffect(() => {
    const token = tokenManager.getAccessToken();

    if (!token) {
      return;
    }

    const manager = createSocketManager({
      onSocketChange: setSocket,
    });

    managerRef.current = manager;
    manager.connect(token);

    return () => {
      managerRef.current = null;
      manager.destroy();
    };
  }, [userSession]);

  useEffect(() => {
    // Backgrounded tabs / lost network commonly stall the socket without
    // ever emitting a disconnect event app code can react to — nudge a
    // reconnect as soon as the tab regains focus or the network returns.
    const handleReconnectNudge = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      managerRef.current?.reconnectNow();
    };

    document.addEventListener('visibilitychange', handleReconnectNudge);
    window.addEventListener('online', handleReconnectNudge);

    return () => {
      document.removeEventListener('visibilitychange', handleReconnectNudge);
      window.removeEventListener('online', handleReconnectNudge);
    };
  }, []);

  const contextValue = useMemo(() => ({ socket }), [socket]);

  return (
    <WebSocketContext value={contextValue}>
      <SocketListeners />
      {children}
    </WebSocketContext>
  );
};
